import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import styles from './HistoryViewer.module.css';
import { X, Clock, RotateCcw, FileText } from 'lucide-react';
import Tooltip from './Tooltip';

interface DocumentSnapshot {
  versionId: string;
  name: string;
  createdAt: string;
  resumeDocument?: any;
}

interface HistoryViewerProps {
  versionId: string;
  onClose: () => void;
  onRestore?: () => void;
}

const HistoryViewer: React.FC<HistoryViewerProps> = ({ versionId, onClose, onRestore }) => {
  const [snapshots, setSnapshots] = useState<DocumentSnapshot[]>([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState<DocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [versionId]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getResumeBranches(versionId);
      
      // The response contains a 'branches' array, not 'snapshots'
      const sortedVersions = response.branches.sort((a: DocumentSnapshot, b: DocumentSnapshot) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setSnapshots(sortedVersions); // The state is named 'snapshots', but will hold version data

      if (sortedVersions.length > 0) {
        setSelectedSnapshot(sortedVersions[0]);
      }
    } catch (err) {
      console.error('Error loading history:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedSnapshot) return;

    setRestoring(true);
    setError(null);

    try {
      // Switch to the selected version instead of restoring
      window.location.href = `/resume/${selectedSnapshot.versionId}`;
      setSuccessMessage('Resume restored successfully!');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

      // Refresh history to show the new restore snapshot
      await loadHistory();
      
      // Call parent callback if provided
      if (onRestore) {
        onRestore();
      }
    } catch (err) {
      console.error('Error restoring version:', err);
      setError('Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const formatTimestamp = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 24) {
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (diffDays < 7) {
      return date.toLocaleString('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const renderDocumentPreview = (document: any) => {
    if (!document || !document.html) {
      return <div>No content available</div>;
    }

    // Create a sanitized version of the HTML for preview
    const previewHtml = document.html;

    return (
      <div 
        className={styles.documentPreview}
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />
    );
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Version History</h2>
          <Tooltip content="Close history viewer">
            <button className={styles.closeButton} onClick={onClose} aria-label="Close history viewer">
              <X size={20} />
            </button>
          </Tooltip>
        </div>

        <div className={styles.content}>
          <div className={styles.timeline}>
            {loading ? (
              <div className={styles.loadingSpinner}>Loading history...</div>
            ) : error ? (
              <div className={styles.errorMessage}>{error}</div>
            ) : snapshots.length === 0 ? (
              <div className={styles.emptyState}>
                <Clock size={48} className={styles.emptyStateIcon} />
                <div className={styles.emptyStateText}>No history yet</div>
                <div className={styles.emptyStateSubtext}>
                  Version history will appear here as changes are made
                </div>
              </div>
            ) : (
              snapshots.map((snapshot, index) => (
                <div
                  key={snapshot.versionId}
                  className={`${styles.timelineItem} ${
                    selectedSnapshot?.versionId === snapshot.versionId ? styles.timelineItemActive : ''
                  }`}
                  onClick={() => setSelectedSnapshot(snapshot)}
                >
                  <div className={styles.timestamp}>
                    {formatTimestamp(snapshot.createdAt)}
                  </div>
                  <div className={styles.reason}>{snapshot.name}</div>
                </div>
              ))
            )}
          </div>

          <div className={styles.preview}>
            {selectedSnapshot ? (
              <>
                <div className={styles.previewHeader}>
                  <div className={styles.previewTitle}>
                    {selectedSnapshot.name}
                  </div>
                  <Tooltip content={snapshots[0]?.versionId === selectedSnapshot.versionId ? "Already the current version" : "Switch to this version"}>
                    <button
                      className={styles.restoreButton}
                      onClick={handleRestore}
                      disabled={restoring || snapshots[0]?.versionId === selectedSnapshot.versionId}
                      aria-label="Switch to this version"
                    >
                      {restoring ? (
                        'Switching...'
                      ) : (
                        <>
                          <RotateCcw size={16} style={{ marginRight: 6, display: 'inline' }} />
                          Switch to this version
                        </>
                      )}
                    </button>
                  </Tooltip>
                </div>
                <div className={styles.previewContent}>
                  {renderDocumentPreview(selectedSnapshot.resumeDocument)}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <FileText size={48} className={styles.emptyStateIcon} />
                <div className={styles.emptyStateText}>Select a snapshot to preview</div>
                <div className={styles.emptyStateSubtext}>
                  Click on any version in the timeline to see its content
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}
    </div>
  );
};

export default HistoryViewer;